import { useAuth0 } from '@auth0/auth0-react'
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import { QueryKeys } from '../../types'
import {
  getHealthPlanSummary,
  getHealthPlanCards,
  getUserHealthPlanOutcomes,
  getCarePlan,
  getCheckList,
  patchCheckListItem,
} from './healthPlan'
import { useSelectUserId } from '../../../../state/selectors'
import {
  HealthPlanCardResponse,
  CarePlanFormatted,
  HealthPlanSummary,
  HealthPlanOutcomeResponse,
  CheckListResponse,
} from './types'
import { sortHealthCards } from '../../../../utils/healthPlan'
import { queryErrorHandler } from '../../../../utils/queryErrorHandler'
import { HealthCardModule } from '@estercare/ester-shared'

export const useGetHealthPlanSummaryQuery = (): UseQueryResult<HealthPlanSummary> => {
  const { getAccessTokenSilently } = useAuth0()
  const userId = useSelectUserId()

  return useQuery({
    queryKey: [QueryKeys.HealthSummaryV2, 'private', userId],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await getHealthPlanSummary({ accessToken })
    },
  })
}

export const useGetUserHealthPlanOutcomesQuery = (): UseQueryResult<HealthPlanOutcomeResponse> => {
  const { getAccessTokenSilently } = useAuth0()
  const userId = useSelectUserId()

  return useQuery({
    queryKey: [QueryKeys.HealthPlanOutcomes, 'private', userId],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await getUserHealthPlanOutcomes({ accessToken })
    },
  })
}

export const useGetHealthPlanCardsQuery = (): UseQueryResult<HealthPlanCardResponse[]> => {
  const { getAccessTokenSilently } = useAuth0()
  const userId = useSelectUserId()

  return useQuery({
    queryKey: [QueryKeys.HealthPlan, 'private', userId, 'cards', 'sorted'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await getHealthPlanCards({ accessToken })
    },
    select: sortHealthCards,
  })
}

export const useGetHealthPlanCardByModuleQuery = (
  module: HealthCardModule
): UseQueryResult<HealthPlanCardResponse | undefined> => {
  const { getAccessTokenSilently } = useAuth0()
  const userId = useSelectUserId()

  return useQuery({
    queryKey: [QueryKeys.HealthPlan, 'private', userId, 'cards', 'sorted'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await getHealthPlanCards({ accessToken })
    },
    select: (cards) => sortHealthCards(cards).find((card) => card.module === module),
  })
}

export const useGetCarePlanQuery = (): UseQueryResult<CarePlanFormatted> => {
  const { getAccessTokenSilently } = useAuth0()
  const userId = useSelectUserId()

  return useQuery({
    queryKey: [QueryKeys.CarePlan, 'private', userId],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await getCarePlan({ accessToken })
    },
  })
}

export const useGetCheckListQuery = (): UseQueryResult<CheckListResponse> => {
  const { getAccessTokenSilently } = useAuth0()
  const userId = useSelectUserId()

  return useQuery({
    queryKey: [QueryKeys.CheckList, 'private', userId],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return await getCheckList({ accessToken })
    },
  })
}

export const useCheckItemMutation = () => {
  const queryClient = useQueryClient()
  const { getAccessTokenSilently } = useAuth0()
  const userId = useSelectUserId()

  return useMutation({
    mutationKey: [QueryKeys.CheckList, 'private', userId],
    mutationFn: async ({ priority }: { priority: number }) => {
      const accessToken = await getAccessTokenSilently()
      return await patchCheckListItem({ accessToken, priority })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CheckList, 'private', userId] })
    },
    onError: () => {
      queryErrorHandler('Failed to patch checklist item')
    },
  })
}
